import classNames from 'classnames'
import { Ellipsis, type LucideIcon } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDropdown } from '@/hooks/useDropdown'
import useBreakpoints from '@/hooks/useBreakpoints'

import './PopOver.scss'

interface PopOverOption {
  label: string
  action: () => void
  state?: any
  icon?: LucideIcon
  isDanger?: boolean
  disabled?: boolean
}

interface PopOverProps {
  options: PopOverOption[]
  className?: string
  style?: React.CSSProperties
  disabled?: boolean
  position?: 'left' | 'right'
  icon?: LucideIcon
}

const PopOver = ({
  options,
  className,
  style,
  disabled = false,
  position: defaultPosition = 'right',
  icon
}: PopOverProps) => {
  const [open, setOpen] = useState(false)
  const [deltaLeft, setDeltaLeft] = useState(0)
  const { isMobile } = useBreakpoints()

  const { anchorRef, menuRef, position } = useDropdown<HTMLButtonElement>({
    open,
    onClose: () => setOpen(false),
    optionsLength: options.length,
    onSelect: () => setOpen(false),
  })

  const calculateDeltaLeft = useCallback(() => {
    const anchorWidth = anchorRef.current?.getBoundingClientRect().width || 0
    const menuWidth = menuRef.current?.getBoundingClientRect().width || 0

    if (defaultPosition === 'left') {
      return menuWidth - anchorWidth
    } else {
      return 0
    }
  }, [defaultPosition, menuRef, anchorRef])

  // Recalculate deltaLeft when popover opens
  useEffect(() => {
    if (!open) return

    setDeltaLeft(calculateDeltaLeft())
  }, [open, calculateDeltaLeft])

  const Icon = icon || Ellipsis

  return (
    <>
      <button
        ref={anchorRef}
        type='button'
        className={classNames('popover-trigger', className)}
        style={style}
        onClick={() => setOpen(o => !o)}
        disabled={disabled}
      >
        <Icon size={!isMobile ? 18 : 24} />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className='popover-menu'
            style={{
              position: 'absolute',
              top: position.top,
              left: position.left - deltaLeft,
              zIndex: 99999,
            }}
          >
            <ul>
              {options.map((opt, i) => {
                const Icon = opt.icon
                return (
                  <li
                    key={i}
                    className={classNames(
                      { danger: opt.isDanger },
                      { disabled: opt.disabled }
                    )}
                    onClick={() => {
                      opt.action()
                      setOpen(false)
                    }}
                  >
                    {Icon && <Icon size={18} style={{ minWidth: 18 }} />}
                    <span>{opt.label}</span>
                  </li>
                )
              })}
            </ul>
          </div>,
          document.body
        )}
    </>
  )
}

export default PopOver
